import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import {
  getFileSignature,
  isPrintableKey,
  makeCancelReasonSchema,
  makeInputSlotProps,
  makeInputSxProps,
  TEXTAREA_MAX_CHARS,
  type MovementError,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import type { Movement } from "@services/movement-services/types";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useCancelMovement } from "../hooks/useCancelMovement";

interface OutletContextType {
  vesselCallDetails: { id: string; vesselId: string };
}

interface CancelMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}

const serviceVesselCall = "bff-vessel-call";
const CancelMovementService: React.FC<CancelMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;
  const [cancelReason, setCancelReason] = useState<string>("");
  const [additionalErrors, setAdditionalErrors] = useState<{
    cancelReason?: string;
    files?: string;
  }>({});
  const [error, setError] = useState<null | string>(null);
  const [cancelAttachmentsUrl, setCancelAttachmentsUrl] = useState<string>();
  const theme = useTheme();

  const cancelReasonSchema = makeCancelReasonSchema(t);
  const inputSlotProps = makeInputSlotProps(theme);
  const sxProps = makeInputSxProps(theme);
  const styles = getSideDrawerStyles(theme);
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const cancelAttachmentFile = useUploadStore(
    (state) => state.filesByKey.cancelAttachmentFile ?? EMPTY_UPLOAD_ENTRIES,
  );

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const additionalUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;

      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);

      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );
  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value: { files?: string } | ((prev: { files?: string }) => { files?: string }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ files: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "CancelMovementService",
          action: "upload_movement_service_document",
          entityType: "movement_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate(
      { file, documentType },
      {
        onSuccess: ({ documentUrl }: { documentUrl: string }) => {
          setError(null);
          setCancelAttachmentsUrl(documentUrl);
        },
      },
    );
  };

  const setCancelAttachmentFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("cancelAttachmentFile");
        return;
      }
      setFilesForKey("cancelAttachmentFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  useEffect(() => {
    setCancelAttachmentFile([]);
  }, []);

  const resetFormValues = () => {
    setCancelReason("");
    setAdditionalErrors({});
    setCancelAttachmentsUrl("");
    setCancelAttachmentFile([]);
  };

  const handleSuccess = async () => {
    onClose("cancelDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-canceled"),
      type: "success",
    });
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getPastMovementServices"],
      });

      // Invalidate and refetch the preview
      const movementServiceId = currentMovements[0]?.id;
      if (movementServiceId) {
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
        await queryClient.refetchQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
      }
    }, 2000);
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["getPastMovementServices"],
    });
    const movementServiceId = currentMovements[0]?.id;
    if (movementServiceId) {
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
      await queryClient.refetchQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
    }
    onClose("cancelDrawer");
    resetFormValues();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };
  const { cancelMovement } = useCancelMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = () => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const validation = cancelReasonSchema.safeParse({ cancelReason });
    if (!validation.success) {
      setAdditionalErrors((prev) => ({
        ...prev,
        cancelReason: validation.error.flatten().fieldErrors.cancelReason?.[0],
      }));
      return;
    }
    const payload: {
      reason: string;
      additionalDocument?: { url: string; type: string; originalFileName: string };
    } = {
      reason: cancelReason,
    };
    if (cancelAttachmentsUrl) {
      payload.additionalDocument = {
        url: cancelAttachmentsUrl || "",
        type: "CANCEL_ATTACHMENTS",
        originalFileName: cancelAttachmentFile[0]?.name || "",
      };
    }
    cancelMovement({
      id: currentMovements[0]?.id || "",
      service: serviceVesselCall,
      payload,
    });
  };

  return (
    <div data-testid="cancel-movement-service-form">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.cancel-title`)}
        onClose={() => {
          onClose("cancelDrawer");
          resetFormValues();
        }}
        submitLabel={t(`vessel-call.movement-services.cancel-button`)}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <Typography
            variant="body1"
            data-testid="movement-service-form-title"
            sx={{ color: "text.primary", pb: 4 }}
          >
            {t(`vessel-call.movement-services.cancel-subtitle`)}
          </Typography>
          <div>
            <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
              <CustomFormLabel>
                {t("vessel-management.vessel-status-page.cancelReason")}
              </CustomFormLabel>
              <Input
                placeholder={t("vessel-management.vessel-status-page.cancelReason")}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={cancelReason}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (cancelReason.length >= TEXTAREA_MAX_CHARS) {
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      cancelReason: t("form-validation.max-length", {
                        label: t("vessel-management.vessel-status-page.cancelReason"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    additionalDescription: undefined,
                  }));
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setCancelReason(value);
                  const result = cancelReasonSchema.safeParse({
                    cancelReason: value,
                  });
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    cancelReason: result.success
                      ? undefined
                      : result.error.flatten().fieldErrors.cancelReason?.[0],
                  }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId={"additional-notes-input"}
              />
              {additionalErrors.cancelReason && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {additionalErrors.cancelReason}
                </p>
              )}
              <FormHelperText sx={charCounterSx}>
                {cancelReason.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>
            <div className="col-span-full mt-6 ">
              <input type="hidden" name="cancelAttachmentFileUrl" />
              <div className={additionalErrors.files ? "border border-red-500 rounded-md p-1" : ""}>
                <DocumentUpload
                  label={t("vessel-management.vessel-status-page.externalAttachments")}
                  testId={"additional-file-upload"}
                  name="cancelAttachmentFile"
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedSideDrawerExtensions}
                  onUpload={(newFiles) =>
                    handleFileUpload({
                      newFiles,
                      setUploadErrors: setAdditionalErrors,
                      setUploadFiles: setCancelAttachmentFile,
                      onUploadDocument: (file) => handleDocumentUpload(file, "CANCEL_ATTACHMENTS"),
                      uploadTracker: additionalUploadSignatureRef,
                    })
                  }
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={cancelAttachmentFile}
                  setFiles={setCancelAttachmentFile}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
              </div>
              {additionalErrors.files && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {additionalErrors.files}
                </p>
              )}
              {cancelAttachmentFile[0] && !additionalErrors.files && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    file={cancelAttachmentFile[0]}
                    fieldName="cancelAttachmentFile"
                    isLoading={documentUploadMutation.isPending}
                    setFiles={() => {
                      setAdditionalErrors((prev) => ({ ...prev, files: undefined }));
                      setCancelAttachmentFile([]);
                      additionalUploadSignatureRef.current = null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default CancelMovementService;
